import AnalyticsSelector, {
  DateRange,
} from '@superfunnel/ui-core/v2/AnalyticsSelector';
import { Calendar } from 'lucide-react';
import { useContext, useEffect, useState } from 'react';
import { useAnalytics } from '../contexts/AnalyticsContext';
import { DashboardContext } from '../contexts/DashboardContext';

interface Props {
  size?: 'x-small' | 'small' | 'normal' | 'large' | 'x-large';
  disabled?: boolean;
  variant?: 'textfield' | 'text';
}

export default function DashboardAnalyticsRangeSelector({
  size: _size = 'normal',
  disabled: _disabled,
}: Props) {
  const { track } = useAnalytics();
  const { analyticsRange, setAnalyticsRange } = useContext(DashboardContext);
  const [customDateRange, setCustomDateRange] = useState<DateRange>();

  // Initialize customDateRange if analyticsRange is a custom range object
  useEffect(() => {
    if (
      typeof analyticsRange === 'object' &&
      analyticsRange.startDate &&
      analyticsRange.endDate
    ) {
      setCustomDateRange({
        from: new Date(analyticsRange.startDate),
        to: new Date(analyticsRange.endDate),
      });
    }
  }, [analyticsRange]);

  // Ensure we have a valid value, default to 'last30Days' if not
  const validRanges = [
    'today',
    'yesterday',
    'last7Days',
    'last30Days',
    'currentMonth',
  ];

  const currentValue = (() => {
    if (
      typeof analyticsRange === 'string' &&
      validRanges.includes(analyticsRange)
    ) {
      return analyticsRange;
    }
    if (
      typeof analyticsRange === 'object' &&
      analyticsRange.startDate &&
      analyticsRange.endDate
    ) {
      // If we have a custom range object, return 'custom'
      return 'custom';
    }
    return 'last30Days';
  })();

  // Reset to valid value if current value is invalid
  if (
    typeof analyticsRange === 'string' &&
    !validRanges.includes(analyticsRange)
  ) {
    setAnalyticsRange('last30Days');
  }

  const handleCustomDateRangeChange = (range: DateRange | undefined) => {
    setCustomDateRange(range);

    if (range) {
      // Convert DateRange to AnalyticsCustomRange format using local timezone
      const customRange = {
        startDate:
          range.from.getFullYear() +
          '-' +
          String(range.from.getMonth() + 1).padStart(2, '0') +
          '-' +
          String(range.from.getDate()).padStart(2, '0'),
        endDate:
          range.to.getFullYear() +
          '-' +
          String(range.to.getMonth() + 1).padStart(2, '0') +
          '-' +
          String(range.to.getDate()).padStart(2, '0'),
      };

      track('analytics_date_range_changed', {
        range_type: 'custom',
        start_date: customRange.startDate,
        end_date: customRange.endDate,
        days_selected:
          Math.ceil(
            (range.to.getTime() - range.from.getTime()) / (1000 * 60 * 60 * 24),
          ) + 1,
      });

      setAnalyticsRange(customRange);
    }
    // If range is undefined (cleared), the analytics range will be handled by the onValueChange in SegmentedControl
  };

  return (
    <AnalyticsSelector
      value={currentValue}
      onValueChange={(v: string) => {
        if (v === 'custom') {
          // The calendar will handle opening automatically
          track('analytics_date_range_selector_opened', {
            range_type: 'custom',
          });
          return;
        }

        track('analytics_date_range_changed', {
          range_type: v,
        });

        setAnalyticsRange(v as any);
      }}
      customDateRange={customDateRange}
      onCustomDateRangeChange={handleCustomDateRangeChange}
      options={[
        { label: '1D', value: 'today' },
        { label: '7D', value: 'last7Days' },
        { label: '1M', value: 'last30Days' },
        {
          label: (
            <span className="inline-flex items-center gap-1">
              <Calendar className="size-4" />
              Custom
            </span>
          ),
          value: 'custom',
        },
      ]}
    />
  );
}
